import React, { useState, useEffect } from 'react';
import { Calendar, Check, Link2, Unlink, Plus, Clock, User, Phone, FileText } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarSettings = () => {
  const [loading, setLoading] = useState(true);
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/calendar/config`),
        axios.get(`${API}/appointments`)
      ]);
      setCalendarConfig(configRes.data);
      setAppointments(appointmentsRes.data.appointments || []);
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectCalendar = async (provider) => {
    setConnecting(provider);
    try {
      const res = await axios.post(`${API}/calendar/connect`, { provider });
      
      if (res.data.auth_url) {
        // Open OAuth window
        window.open(res.data.auth_url, '_blank', 'width=500,height=600');
        toast.info(`Complete authorization in the popup window`);
      } else {
        toast.success(`${provider} calendar connected!`);
        fetchData();
      }
    } catch (err) {
      console.error('Failed to connect:', err);
      toast.error(`Failed to connect ${provider}`);
    } finally {
      setConnecting(null);
    }
  };

  const disconnectCalendar = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect`);
      toast.success('Calendar disconnected');
      fetchData();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calendarOptions = [
    {
      id: 'google',
      name: 'Google Calendar',
      icon: '📅',
      color: 'bg-blue-500',
      description: 'Connect your Google account'
    },
    {
      id: 'apple',
      name: 'Apple Calendar',
      icon: '🍎',
      color: 'bg-gray-500',
      description: 'Connect via iCloud'
    },
    {
      id: 'outlook',
      name: 'Outlook Calendar',
      icon: '📧',
      color: 'bg-sky-500',
      description: 'Connect your Microsoft account'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const connectedProvider = calendarConfig?.connected_provider;

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8" data-testid="calendar-settings-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">CALENDAR</h1>
          <p className="text-muted-foreground mt-1">Connect your calendar to automatically save appointment info after calls</p>
        </div>

        {/* Connection Status */}
        {connectedProvider && (
          <Card className="card-modern p-6 mb-6 border-green-500/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-green-500/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">
                    {calendarOptions.find(c => c.id === connectedProvider)?.name || connectedProvider} Connected
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Appointments will be added automatically after calls
                  </p>
                </div>
              </div>
              <Button 
                onClick={disconnectCalendar}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </Card>
        )}

        {/* Calendar Options */}
        <Card className="card-modern p-6 mb-6">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-400" />
            {connectedProvider ? 'Change Calendar' : 'Connect a Calendar'}
          </h2>
          
          <div className="grid gap-4">
            {calendarOptions.map((cal) => (
              <div 
                key={cal.id}
                className={`p-4 rounded-sm border transition-colors ${
                  connectedProvider === cal.id 
                    ? 'border-green-500 bg-green-500/10' 
                    : 'border-border bg-muted hover:border-[#3F3F46]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-sm ${cal.color}/20 flex items-center justify-center text-2xl`}>
                      {cal.icon}
                    </div>
                    <div>
                      <h3 className="text-foreground font-medium flex items-center gap-2">
                        {cal.name}
                        {connectedProvider === cal.id && (
                          <Badge className="bg-green-500/20 text-green-400 border-0">Connected</Badge>
                        )}
                      </h3>
                      <p className="text-muted-foreground text-sm">{cal.description}</p>
                    </div>
                  </div>
                  
                  {connectedProvider !== cal.id && (
                    <Button 
                      onClick={() => connectCalendar(cal.id)}
                      disabled={connecting === cal.id}
                      className="bg-[#27272A] hover:bg-[#3F3F46]"
                    >
                      {connecting === cal.id ? (
                        <div className="spinner-sm" />
                      ) : (
                        <>
                          <Link2 className="w-4 h-4 mr-2" />
                          Connect
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Appointments */}
        <Card className="card-modern">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Clock className="w-6 h-6 text-orange-400" />
              RECENT APPOINTMENTS
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Appointments collected from Pro calls
            </p>
          </div>
          
          <ScrollArea className="h-[400px]">
            {appointments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No appointments yet</p>
                <p className="text-sm mt-1">Appointments will appear here after Pro collects them from calls</p>
              </div>
            ) : (
              <div className="divide-y divide-[#27272A]">
                {appointments.map((apt, idx) => (
                  <div key={apt.id || idx} className="p-4 hover:bg-white/[0.02]">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
                          <User className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-foreground font-medium">{apt.customer_name || 'Unknown'}</p>
                          <p className="text-muted-foreground text-sm flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            {apt.phone_number || 'No phone'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={`${apt.synced_to_calendar ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'} border-0`}>
                          {apt.synced_to_calendar ? 'Synced' : 'Pending'}
                        </Badge>
                        <p className="text-muted-foreground text-xs mt-1">{formatDate(apt.created_at)}</p>
                      </div>
                    </div>
                    
                    {apt.service_needed && (
                      <div className="ml-13 mt-2 p-3 bg-muted rounded-sm">
                        <p className="text-sm text-muted-foreground flex items-start gap-2">
                          <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {apt.service_needed}
                        </p>
                        {apt.preferred_time && (
                          <p className="text-sm text-foreground mt-1 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Preferred: {apt.preferred_time}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
};

export default CalendarSettings;
